import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Download, FileText, RefreshCw, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { DsButton, DsEmptyState, DsSpinner } from "../../../design-system";
import {
  companyService,
  payrollOpsService,
  salaryStructureService,
  branchService,
} from "../../../services/api";
import { getAuthJson, setAuthJson } from "../../../utils/authStorage";
import { usePermissions } from "../../../rbac/PermissionsContext";
import useConfirmDialog from "../../../hooks/useConfirmDialog";
import { formatInr, formatPayMonthLabel } from "./runPayrollUtils";
import { displayLabel } from "../../../utils/displayLabel";
import {
  applyPfAdminMinimum,
  buildEpfoArrearEcrText,
  buildEpfoEcrText,
  buildEsiArrearRows,
  buildEsiRows,
  buildEsicMcTemplateRows,
  buildPfArrearRows,
  buildPfLiabilityBreakdown,
  buildPfRows,
  buildPfSummaryCards,
  buildPtRows,
  downloadBlob,
  downloadEsicMcTemplateFile,
  downloadTextFile,
  filterRowsByBranch,
  groupPtByState,
  payMonthNow,
  roundRupee,
  shiftPayMonth,
  sumFieldRupee,
} from "./statutoryStatementUtils";

const KIND_META = {
  pf: {
    title: "PF Statements",
    screenKey: "statutory_filing_pf",
    description:
      "PF contribution register from finalised payroll only. EPFO ECR uses 11 fields separated by #~#. Finalise the pay month before filing.",
    codeLabel: "PF Code No.",
    codeKey: "pfCode",
    codeHelp: "Configuration → Enterprise → PF Code No.",
  },
  esi: {
    title: "ESI Statements",
    screenKey: "statutory_filing_esi",
    description:
      "ESI contribution register from finalised payroll only, with official ESIC MC_Template11.xls export (includes prior-month leavers with status code 2).",
    codeLabel: "ESI Code No.",
    codeKey: "esiCode",
    codeHelp: "Configuration → Enterprise → ESI Code No.",
  },
  pt: {
    title: "PT Statements",
    screenKey: "statutory_filing_pt",
    description:
      "Professional Tax register from finalised payroll only, with state-wise portal Excel.",
    codeLabel: null,
    codeKey: null,
    codeHelp: null,
  },
};

function companyFromStorage() {
  const c = getAuthJson("companyData") || {};
  return {
    id: c.id || null,
    name: c.name || c.companyName || "Company",
    pfCode: String(c.pfCode || c.pf_code || "").trim(),
    esiCode: String(c.esiCode || c.esi_code || "").trim(),
  };
}

function pickCompanyCodes(raw, fallback = {}) {
  const src = raw?.company || raw?.data?.company || raw?.data || raw || {};
  return {
    id: src.id || fallback.id || null,
    name: src.name || src.companyName || fallback.name || "Company",
    pfCode: String(src.pfCode || src.pf_code || fallback.pfCode || "").trim(),
    esiCode: String(src.esiCode || src.esi_code || fallback.esiCode || "").trim(),
  };
}

function styleHeaderRow(row) {
  row.eachCell((cell) => {
    cell.font = { bold: true, size: 10 };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE8EEF5" },
    };
    cell.alignment = { vertical: "middle", wrapText: true };
    cell.border = {
      top: { style: "thin", color: { argb: "FFCBD5E1" } },
      bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
      left: { style: "thin", color: { argb: "FFCBD5E1" } },
      right: { style: "thin", color: { argb: "FFCBD5E1" } },
    };
  });
}

function applyMoneyFormat(ws, cols, startRow, endRow) {
  for (let r = startRow; r <= endRow; r++) {
    cols.forEach((c) => {
      const cell = ws.getCell(r, c);
      cell.numFmt = "#,##0";
      cell.alignment = { horizontal: "right" };
    });
  }
}

async function writeWorkbook(filename, buildFn) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();
  await buildFn(wb);
  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(
    filename,
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
  );
}

export default function StatutoryContributionStatementPanel({
  kind = "pf",
  darkMode = false,
}) {
  const { confirm, dialogNode } = useConfirmDialog();
  const meta = KIND_META[kind] || KIND_META.pf;
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canExport =
    isAdmin ||
    hasScreenAccess("payroll", meta.screenKey, "export") ||
    hasScreenAccess("payroll", "statutory_filing", "export") ||
    hasScreenAccess("payroll", "process_payroll", "export");

  const [payMonth, setPayMonth] = useState("");
  const [finalizedMonths, setFinalizedMonths] = useState([]);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState("__ALL__");
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [register, setRegister] = useState(null);
  const [company, setCompany] = useState(companyFromStorage);
  const [adminChargesMinimum, setAdminChargesMinimum] = useState(500);
  /** PF/ESI only — Regular keeps current behaviour; Arrears = LOP reversal + salary revision. */
  const [filingMode, setFilingMode] = useState("regular");
  const [pfRates, setPfRates] = useState({});
  const [esiRates, setEsiRates] = useState({});
  const supportsArrearsMode = kind === "pf" || kind === "esi";
  const isArrears = supportsArrearsMode && filingMode === "arrears";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = companyFromStorage();
        if (stored.id) {
          const res = await companyService.getCompanyData(stored.id, { skipCache: true });
          if (!cancelled) {
            const next = pickCompanyCodes(res, stored);
            setCompany(next);
            // Keep session companyData in sync so PF/ESI codes stay available across screens
            try {
              const prev = getAuthJson("companyData") || {};
              setAuthJson("companyData", {
                ...prev,
                id: next.id || prev.id,
                name: next.name || prev.name,
                pfCode: next.pfCode || prev.pfCode || null,
                esiCode: next.esiCode || prev.esiCode || null,
              });
            } catch {
              /* storage optional */
            }
          }
        } else if (!cancelled) {
          setCompany(stored);
        }
      } catch {
        /* keep storage fallback */
      }
      try {
        const statRes = await salaryStructureService.getStatutoryConfig();
        const rates =
          statRes?.data?.configJson?.rates || statRes?.data?.rates || {};
        const pf = rates.pf || {};
        const esi = rates.esi || {};
        const min = Number(pf.adminChargesMinimum ?? pf.adminMinimum ?? 500);
        if (!cancelled && Number.isFinite(min)) setAdminChargesMinimum(min);
        if (!cancelled) {
          setPfRates(pf);
          setEsiRates(esi);
        }
      } catch {
        /* default 500 */
      }
      try {
        const brRes = await branchService.getBranches(false);
        const list = brRes?.data || brRes || [];
        const names = (Array.isArray(list) ? list : [])
          .map((b) => String(b.name || b.branchName || "").trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        if (!cancelled) setBranches(names);
      } catch {
        /* branches optional — filled from register after load */
      }
      try {
        setMonthsLoading(true);
        const monthsRes = await payrollOpsService.listFinalizedPayrollMonths();
        const list = monthsRes?.data || monthsRes || [];
        const months = (Array.isArray(list) ? list : [])
          .map((m) => ({
            payMonth: String(m.payMonth || "").slice(0, 7),
            id: m.id,
            finalizedAt: m.finalizedAt,
          }))
          .filter((m) => /^\d{4}-\d{2}$/.test(m.payMonth));
        if (!cancelled) {
          setFinalizedMonths(months);
          const preferred = payMonthNow();
          const pick =
            months.find((m) => m.payMonth === preferred)?.payMonth ||
            months[0]?.payMonth ||
            "";
          setPayMonth(pick);
          if (!months.length) {
            setError(
              "No finalised payroll months yet. Complete Process Payroll and Finalise Payroll for a month before generating statutory filing."
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          setFinalizedMonths([]);
          setPayMonth("");
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load finalised payroll months"
          );
        }
      } finally {
        if (!cancelled) setMonthsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    const month = String(payMonth || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month)) {
      setError("Select a finalised pay month");
      return;
    }
    const allowed = finalizedMonths.some((m) => m.payMonth === month);
    if (!allowed) {
      setRegister(null);
      setHasLoaded(true);
      setError(
        `${formatPayMonthLabel(month)} is not finalised. Statutory filing is available only after Finalise Payroll.`
      );
      return;
    }
    setLoading(true);
    setError("");
    try {
      const runRes = await payrollOpsService.findRun(month);
      const run = runRes?.data || runRes?.run || runRes;
      const runId = Number(run?.id);
      if (!runId) {
        setRegister(null);
        setHasLoaded(true);
        setError(
          `No payroll run found for ${formatPayMonthLabel(month)}. Process and finalise payroll first.`
        );
        return;
      }
      if (!run.finalizedAt) {
        setRegister(null);
        setHasLoaded(true);
        setError(
          `${formatPayMonthLabel(month)} is not finalised. Complete Finalise Payroll, then reload.`
        );
        return;
      }
      const regRes = await payrollOpsService.getSalaryRegister(runId, month);
      const data = regRes?.data || regRes;
      if (!data?.rows?.length) {
        setRegister(data || null);
        setHasLoaded(true);
        setError(
          `No processed employees for ${formatPayMonthLabel(month)}. Complete Process Payroll, finalise, then reload.`
        );
        return;
      }
      setRegister(data);
      setHasLoaded(true);
      const fromReg = [
        ...new Set(
          (data.rows || [])
            .map((r) => String(r.branch || "").trim())
            .filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b));
      if (fromReg.length) {
        setBranches((prev) => {
          const merged = [...new Set([...(prev || []), ...fromReg])].sort((a, b) =>
            a.localeCompare(b)
          );
          return merged;
        });
      }
    } catch (err) {
      setRegister(null);
      setHasLoaded(true);
      setError(err?.response?.data?.message || err?.message || "Failed to load statement");
    } finally {
      setLoading(false);
    }
  }, [payMonth, finalizedMonths]);

  const allKindRows = useMemo(() => {
    const source = register?.rows || [];
    if (kind === "esi") {
      return isArrears ? buildEsiArrearRows(source, esiRates) : buildEsiRows(source);
    }
    if (kind === "pt") return buildPtRows(source);
    return isArrears ? buildPfArrearRows(source, pfRates) : buildPfRows(source);
  }, [register, kind, isArrears, pfRates, esiRates]);

  const statementButtonLabel = isArrears ? "Arrears Statement" : "Regular Statement";
  const filingLabel = useMemo(() => {
    if (kind === "pf") return isArrears ? "Arrears ECR" : "Regular ECR";
    if (kind === "esi") return isArrears ? "Arrears MC Return" : "Regular MC Return";
    return "Download PT portal Excel";
  }, [kind, isArrears]);

  const rows = useMemo(
    () => filterRowsByBranch(allKindRows, branchFilter),
    [allKindRows, branchFilter]
  );

  // Table-only search; summary cards and exports keep using `rows`.
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const tableRows = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase().replace(/\s+/g, " ");
    if (!q) return rows;
    const qCompact = q.replace(/\s/g, "");
    const idsByCode = new Map(
      (register?.rows || []).map((r) => [r.empCode, `${r.uan || ""} ${r.pan || ""}`])
    );
    return rows.filter((r) => {
      if (String(r.name || "").toLowerCase().includes(q)) return true;
      const ids = `${r.empCode || ""} ${r.uan || ""} ${idsByCode.get(r.empCode) || ""}`
        .toLowerCase()
        .replace(/\s/g, "|");
      return ids.includes(qCompact);
    });
  }, [rows, deferredSearch, register]);

  const adminMinForView =
    kind === "pf" && (isArrears || (branchFilter && branchFilter !== "__ALL__"))
      ? 0
      : adminChargesMinimum;

  const pfAdmin = useMemo(
    () => (kind === "pf" ? applyPfAdminMinimum(rows, adminMinForView) : null),
    [kind, rows, adminMinForView]
  );

  const pfLiability = useMemo(
    () => (kind === "pf" ? buildPfLiabilityBreakdown(rows, adminMinForView) : null),
    [kind, rows, adminMinForView]
  );

  const pfSummary = useMemo(
    () => (kind === "pf" ? buildPfSummaryCards(rows, adminMinForView) : null),
    [kind, rows, adminMinForView]
  );

  const totals = useMemo(() => {
    if (kind === "esi") {
      return {
        employees: rows.length,
        employeeShare: sumFieldRupee(rows, "employeeEsi"),
        employerShare: sumFieldRupee(rows, "employerEsi"),
        total: sumFieldRupee(rows, "totalEsi"),
        adminPayable: 0,
      };
    }
    if (kind === "pt") {
      return {
        employees: rows.length,
        employeeShare: sumFieldRupee(rows, "pt"),
        employerShare: 0,
        total: sumFieldRupee(rows, "pt"),
        adminPayable: 0,
      };
    }
    return {
      employees: pfSummary?.totalEmployees || 0,
      employeeShare: pfSummary?.pfAmount || 0,
      employerEpf: pfSummary?.epfAmount || 0,
      employerShare: sumFieldRupee(rows, "employerShare"),
      adminPayable: pfSummary?.adminCharges || 0,
      edli: pfSummary?.edliAmount || 0,
      eps: pfSummary?.epsAmount || 0,
      account1: pfLiability?.account1 || 0,
      total: pfSummary?.totalPayable || 0,
    };
  }, [rows, kind, pfLiability, pfSummary]);

  const assertExport = () => {
    if (!canExport) {
      toast.error("You do not have permission to export this statement.");
      return false;
    }
    if (!finalizedMonths.some((m) => m.payMonth === String(payMonth).slice(0, 7))) {
      toast.error("Export is allowed only for finalised payroll months.");
      return false;
    }
    if (!rows.length) {
      toast.error("Nothing to export for this month.");
      return false;
    }
    return true;
  };

  const exportRegisterExcel = async () => {
    if (!assertExport()) return;
    try {
      const monthLabel = formatPayMonthLabel(payMonth);
      const codeVal = meta.codeKey ? company[meta.codeKey] : "";
      const stamp = String(payMonth).replace("-", "");

      await writeWorkbook(
        `${kind.toUpperCase()}_${isArrears ? "Arrears" : "Regular"}_Statement_${stamp}.xlsx`,
        async (wb) => {
        const ws = wb.addWorksheet(isArrears ? "Arrears" : "Register");
        ws.columns = [{ width: 18 }, { width: 28 }, { width: 22 }, { width: 16 }, { width: 14 }];

        const title = ws.addRow([
          `${meta.title}${isArrears ? " — Arrears" : " — Regular"}`,
        ]);
        title.font = { bold: true, size: 14 };
        ws.addRow(["Company", company.name]);
        if (meta.codeLabel) {
          ws.addRow([
            meta.codeLabel,
            codeVal || `Not set — ${meta.codeHelp || "Configuration → Enterprise"}`,
          ]);
        }
        ws.addRow(["Pay month", monthLabel]);
        if (isArrears) {
          ws.addRow([
            "Arrears scope",
            "LOP reversal + Salary revision arrears (this finalised month)",
          ]);
        }
        ws.addRow(["Generated at", new Date().toLocaleString("en-IN")]);
        ws.addRow([]);

        if (kind === "esi") {
          const header = ws.addRow([
            "Emp Code",
            "Employee Name",
            "ESI IP Number",
            "Branch",
            "Paid Days",
            isArrears ? "Arrears wages" : "Gross",
            ...(isArrears
              ? ["Revision arrears", "LOP reversal"]
              : []),
            "ESI (Employee)",
            "ESI (Employer)",
            "Total ESI",
          ]);
          styleHeaderRow(header);
          ws.columns = [
            { width: 12 },
            { width: 24 },
            { width: 18 },
            { width: 16 },
            { width: 11 },
            { width: 14 },
            ...(isArrears ? [{ width: 14 }, { width: 12 }] : []),
            { width: 14 },
            { width: 14 },
            { width: 12 },
          ];
          const start = ws.rowCount + 1;
          rows.forEach((r) => {
            ws.addRow([
              r.empCode,
              r.name,
              r.esiNo || "",
              r.branch || "",
              roundRupee(r.paidDays),
              roundRupee(r.grossPay),
              ...(isArrears
                ? [roundRupee(r.revisionArrears), roundRupee(r.lopReversal)]
                : []),
              roundRupee(r.employeeEsi),
              roundRupee(r.employerEsi),
              roundRupee(r.totalEsi),
            ]);
          });
          const end = ws.rowCount;
          const moneyCols = isArrears ? [6, 7, 8, 9, 10, 11] : [6, 7, 8, 9];
          applyMoneyFormat(ws, moneyCols, start, end);
          const tot = ws.addRow(
            isArrears
              ? [
                  "Totals",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  totals.employeeShare,
                  totals.employerShare,
                  totals.total,
                ]
              : [
                  "Totals",
                  "",
                  "",
                  "",
                  "",
                  "",
                  totals.employeeShare,
                  totals.employerShare,
                  totals.total,
                ]
          );
          tot.font = { bold: true };
          applyMoneyFormat(
            ws,
            isArrears ? [9, 10, 11] : [7, 8, 9],
            tot.number,
            tot.number
          );
        } else if (kind === "pt") {
          const header = ws.addRow([
            "Emp Code",
            "Employee Name",
            "Branch",
            "State",
            "Paid Days",
            "Gross",
            "Professional Tax",
          ]);
          styleHeaderRow(header);
          ws.columns = [
            { width: 12 },
            { width: 24 },
            { width: 16 },
            { width: 16 },
            { width: 11 },
            { width: 12 },
            { width: 16 },
          ];
          const start = ws.rowCount + 1;
          rows.forEach((r) => {
            ws.addRow([
              r.empCode,
              r.name,
              r.branch || "",
              r.state || "",
              roundRupee(r.paidDays),
              roundRupee(r.grossPay),
              roundRupee(r.pt),
            ]);
          });
          const end = ws.rowCount;
          applyMoneyFormat(ws, [6, 7], start, end);
          const tot = ws.addRow(["Totals", "", "", "", "", "", totals.total]);
          tot.font = { bold: true };
          applyMoneyFormat(ws, [7], tot.number, tot.number);
        } else if (isArrears) {
          const header = ws.addRow([
            "Emp Code",
            "Employee Name",
            "UAN",
            "Branch",
            "Revision arrears",
            "LOP reversal",
            "ARREAR_EPF_WAGES",
            "ARREAR_EPS_WAGES",
            "ARREAR_EDLI_WAGES",
            "ARREAR_EPF_EE_SHARE",
            "ARREAR_EPF_ER_SHARE",
            "ARREAR_EPS_SHARE",
          ]);
          styleHeaderRow(header);
          ws.columns = [
            { width: 12 },
            { width: 22 },
            { width: 16 },
            { width: 14 },
            { width: 14 },
            { width: 12 },
            { width: 16 },
            { width: 16 },
            { width: 16 },
            { width: 18 },
            { width: 18 },
            { width: 16 },
          ];
          const start = ws.rowCount + 1;
          rows.forEach((r) => {
            ws.addRow([
              r.empCode,
              r.name,
              r.uan || "",
              r.branch || "",
              roundRupee(r.revisionArrears),
              roundRupee(r.lopReversal),
              roundRupee(r.pfWages),
              roundRupee(r.epsWages),
              roundRupee(r.edliWages),
              roundRupee(r.employeePf),
              roundRupee(r.employerEpf),
              roundRupee(r.eps),
            ]);
          });
          const end = ws.rowCount;
          applyMoneyFormat(ws, [5, 6, 7, 8, 9, 10, 11, 12], start, end);
          const tot = ws.addRow([
            "Totals",
            "",
            "",
            "",
            sumFieldRupee(rows, "revisionArrears"),
            sumFieldRupee(rows, "lopReversal"),
            sumFieldRupee(rows, "pfWages"),
            sumFieldRupee(rows, "epsWages"),
            sumFieldRupee(rows, "edliWages"),
            sumFieldRupee(rows, "employeePf"),
            sumFieldRupee(rows, "employerEpf"),
            sumFieldRupee(rows, "eps"),
          ]);
          tot.font = { bold: true };
          applyMoneyFormat(ws, [5, 6, 7, 8, 9, 10, 11, 12], tot.number, tot.number);
        } else {
          const liability =
            pfLiability || buildPfLiabilityBreakdown(rows, adminMinForView);
          const header = ws.addRow([
            "Emp Code",
            "Employee Name",
            "UAN",
            "PF Member ID",
            "Branch",
            "Paid Days",
            "Gross",
            "PF Wages",
            "PF (Employee)",
            "EPF (Employer)",
            "EPS",
            "EDLI",
            "Admin Charges",
          ]);
          styleHeaderRow(header);
          ws.columns = [
            { width: 12 },
            { width: 22 },
            { width: 16 },
            { width: 14 },
            { width: 14 },
            { width: 10 },
            { width: 12 },
            { width: 12 },
            { width: 14 },
            { width: 14 },
            { width: 10 },
            { width: 10 },
            { width: 14 },
          ];
          const start = ws.rowCount + 1;
          rows.forEach((r) => {
            ws.addRow([
              r.empCode,
              r.name,
              r.uan || "",
              r.pfAccountNo || "",
              r.branch || "",
              roundRupee(r.paidDays),
              roundRupee(r.grossPay),
              roundRupee(r.pfWages),
              roundRupee(r.employeePf),
              roundRupee(r.employerEpf),
              roundRupee(r.eps),
              roundRupee(r.edli),
              roundRupee(r.adminCharges),
            ]);
          });
          const end = ws.rowCount;
          applyMoneyFormat(ws, [7, 8, 9, 10, 11, 12, 13], start, end);
          const tot = ws.addRow([
            "Totals",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            liability.employeePf,
            liability.employerEpf,
            liability.account10,
            liability.account21,
            liability.adminCalculated,
          ]);
          tot.font = { bold: true };
          applyMoneyFormat(ws, [9, 10, 11, 12, 13], tot.number, tot.number);

          ws.addRow([]);
          const liabilityTitle = ws.addRow(["Total PF Liability (EPFO Accounts)"]);
          liabilityTitle.font = { bold: true, size: 12 };
          ws.addRow(["Particulars", "Amount (₹)"]).font = { bold: true };

          const liabilityRows = [
            [
              "PF + EPF (Account No. 1)",
              liability.account1,
              `= PF Employee ${liability.employeePf} + EPF Employer ${liability.employerEpf}`,
            ],
            ["EPS (Account No. 10)", liability.account10, ""],
            [
              "Admin Charges (Account No. 2)",
              liability.account2,
              liability.appliedMinimum
                ? `Calculated ${liability.adminCalculated}; min ${liability.adminMinimum} applied`
                : `Calculated ${liability.adminCalculated} (≥ min ${liability.adminMinimum})`,
            ],
            ["EDLI (Account No. 21)", liability.account21, ""],
            ["Total PF Payable", liability.totalPayable, "Account 1 + 10 + 2 + 21"],
          ];
          liabilityRows.forEach(([label, amount, note], idx) => {
            const row = ws.addRow([label, amount, note]);
            row.getCell(2).numFmt = "#,##0";
            row.getCell(2).alignment = { horizontal: "right" };
            if (idx === liabilityRows.length - 1) {
              row.font = { bold: true };
            }
          });
          ws.getColumn(1).width = Math.max(ws.getColumn(1).width || 12, 36);
          ws.getColumn(3).width = 48;
        }
      });
      toast.success(
        isArrears ? "Arrears Statement exported" : "Regular Statement exported"
      );
    } catch (err) {
      toast.error(err?.message || "Export failed");
    }
  };

  const exportFilingFile = async () => {
    if (!assertExport()) return;
    const stamp = String(payMonth).replace("-", "");
    const monthLabel = formatPayMonthLabel(payMonth);

    try {
      if (kind === "pf") {
        const withUan = [];
        const withoutUan = [];
        for (const r of rows) {
          const uan = String(r.uan || "").replace(/\D/g, "");
          if (uan.length >= 12) withUan.push(r);
          else withoutUan.push(r);
        }

        if (!withUan.length) {
          toast.error(
            `None of the ${rows.length} PF employee${rows.length === 1 ? "" : "s"} has a valid 12-digit UAN. Update UAN in Employee master, then reload.`
          );
          return;
        }

        if (withoutUan.length) {
          const sample = withoutUan
            .slice(0, 8)
            .map((r) => r.empCode || r.name || "—")
            .join(", ");
          const more =
            withoutUan.length > 8 ? ` and ${withoutUan.length - 8} more` : "";
          const ok = await confirm({
            title: "ECR warning",
            message:
              `Only employees with a valid 12-digit UAN will be included.\n\n` +
              `Included in ECR: ${withUan.length}\n` +
              `Skipped (no / invalid UAN): ${withoutUan.length}\n` +
              `Skipped: ${sample}${more}\n\n` +
              `Update missing UANs in Employee master before filing if those members must appear on EPFO.\n\n` +
              `Continue and download ECR for the ${withUan.length} UAN record${withUan.length === 1 ? "" : "s"} only?`,
            confirmText: "Continue",
            cancelText: "Cancel",
          });
          if (!ok) return;
          toast(
            `ECR will include ${withUan.length} of ${rows.length} employees — ${withoutUan.length} skipped without UAN.`,
            { icon: "⚠️", duration: 6000 }
          );
        }

        if (!company.pfCode) {
          toast.error(
            "Set PF Code No. under Configuration → Enterprise, then reload this page before filing."
          );
          return;
        }

        const text = isArrears
          ? buildEpfoArrearEcrText(withUan)
          : buildEpfoEcrText(withUan);
        if (!text.trim()) {
          toast.error("No rows with valid 12-digit UAN for ECR. Update employee UAN and reload.");
          return;
        }
        downloadTextFile(
          `${isArrears ? "Arrear_ECR" : "ECR"}_${company.pfCode}_${stamp}.txt`,
          text
        );
        toast.success(
          `EPFO ${isArrears ? "Arrear ECR (8 fields)" : "ECR"} downloaded (${withUan.length} of ${rows.length} employee${
            rows.length === 1 ? "" : "s"
          }, #~# format)${
            withoutUan.length ? ` · ${withoutUan.length} skipped (no UAN)` : ""
          }`
        );
        return;
      }

      if (kind === "esi") {
        let previousEsiRows = [];
        let masterExitRows = [];

        // Prior-month Left Service rows belong on the Regular MC return only.
        if (!isArrears) {
          const prevMonth = shiftPayMonth(payMonth, -1);
          try {
            const exitRes = await payrollOpsService.listEsicPriorMonthExits(payMonth);
            const exitData = exitRes?.data || exitRes;
            masterExitRows = Array.isArray(exitData?.exits) ? exitData.exits : [];
          } catch {
            /* master exits optional — still try previous payroll */
          }

          if (prevMonth) {
            try {
              const prevRunRes = await payrollOpsService.findRun(prevMonth);
              const prevRun = prevRunRes?.data || prevRunRes?.run || prevRunRes;
              const prevRunId = Number(prevRun?.id);
              if (prevRunId) {
                const prevRegRes = await payrollOpsService.getSalaryRegister(
                  prevRunId,
                  prevMonth
                );
                const prevData = prevRegRes?.data || prevRegRes;
                previousEsiRows = buildEsiRows(prevData?.rows || []);
              }
            } catch {
              /* previous month optional — still export current IPs + master exits */
            }
          }
        }

        const mcRows = buildEsicMcTemplateRows({
          currentEsiRows: rows,
          previousEsiRows,
          masterExitRows,
          payMonth,
        });
        if (!mcRows.length) {
          toast.error(
            "No employees with a valid 10-digit ESI IP Number. Update ESI numbers and reload."
          );
          return;
        }
        if (!company.esiCode) {
          toast.error(
            "Set ESI Code No. under Configuration → Enterprise, then reload this page before filing."
          );
          return;
        }
        const result = await downloadEsicMcTemplateFile(
          `${isArrears ? "Arrear_MC" : "MC"}_${company.esiCode}_${stamp}.xls`,
          mcRows
        );
        const exitNote =
          !isArrears && result.exitCount
            ? ` (includes ${result.exitCount} Left Service exit${
                result.exitCount === 1 ? "" : "s"
              } from ${formatPayMonthLabel(shiftPayMonth(payMonth, -1)) || "prior month"})`
            : "";
        toast.success(
          `ESIC ${isArrears ? "Arrears MC Return" : "Regular MC Return"} downloaded${exitNote}`
        );
        return;
      }

      // PT state-wise portal workbook
      const byState = groupPtByState(rows);
      const states = Object.keys(byState);
      if (!states.length) {
        toast.error("No PT rows to export");
        return;
      }
      await writeWorkbook(`PT_Portal_${stamp}.xlsx`, async (wb) => {
        const summary = wb.addWorksheet("Summary");
        summary.addRow(["Professional Tax — State-wise Portal Export"]).font = {
          bold: true,
          size: 14,
        };
        summary.addRow(["Company", company.name]);
        summary.addRow(["Pay month", monthLabel]);
        summary.addRow(["Generated at", new Date().toLocaleString("en-IN")]);
        summary.addRow([]);
        const sh = summary.addRow(["State", "Employees", "PT Amount"]);
        styleHeaderRow(sh);
        summary.columns = [{ width: 22 }, { width: 12 }, { width: 14 }];
        states.forEach((state) => {
          const list = byState[state];
          summary.addRow([state, list.length, sumFieldRupee(list, "pt")]);
        });
        const grand = summary.addRow(["Grand Total", rows.length, totals.total]);
        grand.font = { bold: true };

        states.forEach((state) => {
          const safe = String(state).replace(/[\\/*?[\]:]/g, " ").slice(0, 28) || "State";
          const ws = wb.addWorksheet(safe);
          ws.addRow([`PT Portal — ${state}`]).font = { bold: true, size: 12 };
          ws.addRow(["Company", company.name]);
          ws.addRow(["Pay month", monthLabel]);
          ws.addRow([]);
          const header = ws.addRow([
            "Emp Code",
            "Employee Name",
            "Branch",
            "State",
            "Paid Days",
            "Gross Wages",
            "PT Amount",
          ]);
          styleHeaderRow(header);
          ws.columns = [
            { width: 12 },
            { width: 24 },
            { width: 16 },
            { width: 16 },
            { width: 10 },
            { width: 12 },
            { width: 12 },
          ];
          const list = byState[state];
          const start = ws.rowCount + 1;
          list.forEach((r) => {
            ws.addRow([
              r.empCode,
              r.name,
              r.branch || "",
              r.state || "",
              roundRupee(r.paidDays),
              roundRupee(r.grossPay),
              roundRupee(r.pt),
            ]);
          });
          applyMoneyFormat(ws, [6, 7], start, ws.rowCount);
          const tot = ws.addRow(["Totals", "", "", "", "", "", sumFieldRupee(list, "pt")]);
          tot.font = { bold: true };
        });
      });
      toast.success("PT state-wise portal Excel downloaded");
    } catch (err) {
      toast.error(err?.message || "Filing export failed");
    }
  };

  const cardCls =
    "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]";

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      {dialogNode}
      <div className={`${cardCls} shrink-0 p-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="ds-h4">{meta.title}</h3>
            <p className="mt-1 max-w-2xl text-xs text-[hsl(var(--ds-muted-fg))]">{meta.description}</p>
            <p className="mt-2 text-xs text-[hsl(var(--ds-muted-fg))]">
              {company.name}
              {meta.codeLabel ? (
                <>
                  {" · "}
                  {meta.codeLabel}:{" "}
                  <span className="font-medium text-[hsl(var(--ds-fg))]">
                    {company[meta.codeKey] ||
                      `Not set — save under ${meta.codeHelp || "Configuration → Enterprise"}`}
                  </span>
                </>
              ) : null}
            </p>
            {kind === "pf" && pfAdmin?.appliedMinimum ? (
              <p className="mt-1 text-[11px] text-amber-700">
                Admin charges payable ₹{formatInr(pfAdmin.adminPayable)} (minimum ₹
                {formatInr(pfAdmin.adminMinimum)} applied; calculated ₹
                {formatInr(pfAdmin.adminCalculated)}).
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block text-xs">
              <span className="mb-1 block font-medium text-[hsl(var(--ds-muted-fg))]">
                Pay month (finalised only)
              </span>
              <select
                value={payMonth}
                disabled={monthsLoading || !finalizedMonths.length}
                onChange={(e) => {
                  setPayMonth(e.target.value);
                  setHasLoaded(false);
                  setRegister(null);
                  setError("");
                }}
                className="h-9 min-w-[180px] rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-sm disabled:opacity-50"
              >
                {!finalizedMonths.length ? (
                  <option value="">No finalised months</option>
                ) : (
                  finalizedMonths.map((m) => (
                    <option key={m.payMonth} value={m.payMonth}>
                      {formatPayMonthLabel(m.payMonth)}
                    </option>
                  ))
                )}
              </select>
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-medium text-[hsl(var(--ds-muted-fg))]">Branch</span>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="h-9 min-w-[160px] rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-sm"
              >
                <option value="__ALL__">All branches</option>
                {branches.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
            {supportsArrearsMode ? (
              <fieldset className="block text-xs">
                <legend className="mb-1 block font-medium text-[hsl(var(--ds-muted-fg))]">
                  Return type
                </legend>
                <div
                  className="flex h-9 items-center gap-3 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3"
                  role="radiogroup"
                  aria-label="Regular or Arrears"
                >
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-[hsl(var(--ds-fg))]">
                    <input
                      type="radio"
                      name={`filing-mode-${kind}`}
                      value="regular"
                      checked={filingMode === "regular"}
                      onChange={() => {
                        setFilingMode("regular");
                        setHasLoaded(false);
                        setRegister(null);
                        setError("");
                      }}
                    />
                    Regular
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-[hsl(var(--ds-fg))]">
                    <input
                      type="radio"
                      name={`filing-mode-${kind}`}
                      value="arrears"
                      checked={filingMode === "arrears"}
                      onChange={() => {
                        setFilingMode("arrears");
                        setHasLoaded(false);
                        setRegister(null);
                        setError("");
                      }}
                    />
                    Arrears
                  </label>
                </div>
              </fieldset>
            ) : null}
            <DsButton
              type="button"
              variant="primary"
              onClick={load}
              disabled={loading || monthsLoading || !payMonth || !finalizedMonths.length}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Load
            </DsButton>
            <DsButton
              type="button"
              variant="outline"
              onClick={exportRegisterExcel}
              disabled={loading || !hasLoaded || !rows.length || !canExport}
            >
              <Download size={14} />
              {supportsArrearsMode ? statementButtonLabel : "Export register"}
            </DsButton>
            <DsButton
              type="button"
              variant="outline"
              onClick={exportFilingFile}
              disabled={loading || !hasLoaded || !rows.length || !canExport}
            >
              <FileText size={14} />
              {filingLabel}
            </DsButton>
            <label className="block text-xs sm:ml-auto">
              <span className="mb-1 block font-medium text-[hsl(var(--ds-muted-fg))]">
                Search employee
              </span>
              <span className="relative block">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setSearch("");
                  }}
                  disabled={!hasLoaded || !rows.length}
                  placeholder="Name, emp code, UAN or PAN"
                  aria-label="Search employee by name, emp code, UAN or PAN"
                  className="h-9 w-[260px] rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] pl-8 pr-8 text-sm outline-none transition-colors placeholder:text-[hsl(var(--ds-muted-fg))] focus:border-[hsl(var(--ds-primary))] disabled:opacity-50"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                  >
                    <X size={14} />
                  </button>
                ) : null}
              </span>
            </label>
          </div>
        </div>
      </div>

      {hasLoaded && kind === "pf" && pfSummary ? (
        <div className="shrink-0 overflow-x-auto">
          <div
            className={`grid min-w-[1100px] gap-2 ${
              isArrears ? "grid-cols-7" : "grid-cols-10"
            }`}
          >
            {(isArrears
              ? [
                  ["Employees", pfSummary.totalEmployees],
                  ["PF applicable", pfSummary.pfApplicableCount],
                  ["EPS applicable", pfSummary.epsApplicableCount],
                  ["PF (EE)", formatInr(pfSummary.pfAmount)],
                  ["EPF (ER)", formatInr(pfSummary.epfAmount)],
                  ["EPS", formatInr(pfSummary.epsAmount)],
                  ["Total EE+ER", formatInr(roundRupee(pfSummary.pfAmount + pfSummary.epfAmount + pfSummary.epsAmount))],
                ]
              : [
                  ["Employees", pfSummary.totalEmployees],
                  ["PF applicable", pfSummary.pfApplicableCount],
                  ["EPS applicable", pfSummary.epsApplicableCount],
                  ["EDLI count", pfSummary.edliCount],
                  ["PF amount", formatInr(pfSummary.pfAmount)],
                  ["EPF", formatInr(pfSummary.epfAmount)],
                  ["EPS", formatInr(pfSummary.epsAmount)],
                  ["Admin charges", formatInr(pfSummary.adminCharges)],
                  ["EDLI", formatInr(pfSummary.edliAmount)],
                  ["Total PF Payable", formatInr(pfSummary.totalPayable)],
                ]
            ).map(([label, value]) => (
              <div key={label} className={`${cardCls} p-2.5`}>
                <p className="text-[9px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))] leading-tight">
                  {label}
                </p>
                <p className="mt-1 text-sm font-bold tabular-nums leading-tight">{value}</p>
              </div>
            ))}
          </div>
        </div>
      ) : hasLoaded && kind !== "pf" ? (
        <div className="grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Employees", totals.employees],
            [kind === "pt" ? "PT total" : "Employee share", formatInr(totals.employeeShare)],
            ...(kind === "pt" ? [] : [["Employer share", formatInr(totals.employerShare)]]),
            ["Grand total", formatInr(totals.total)],
          ].map(([label, value]) => (
            <div key={label} className={`${cardCls} p-3`}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                {label}
              </p>
              <p className="mt-1 text-sm font-bold tabular-nums">{value}</p>
            </div>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className={`${cardCls} flex min-h-0 flex-1 items-center justify-center`}>
          <DsSpinner />
        </div>
      ) : !hasLoaded ? (
        <div className={`${cardCls} flex min-h-0 flex-1 items-center justify-center p-8`}>
          <DsEmptyState
            title={
              monthsLoading
                ? "Loading finalised months…"
                : !finalizedMonths.length
                  ? "No finalised payroll months"
                  : "Select filters and load"
            }
            description={
              !monthsLoading && !finalizedMonths.length
                ? "Statutory PF / ESI / PT filing opens only after you Finalise Payroll for a month. Process all employees, settle disbursements, finalise, then return here."
                : "Choose a finalised pay month and branch (or All branches), then click Load."
            }
          />
        </div>
      ) : error && !rows.length ? (
        <div className={`${cardCls} flex min-h-0 flex-1 items-center justify-center p-8`}>
          <DsEmptyState title="No statement data" description={error} />
        </div>
      ) : !rows.length ? (
        <div className={`${cardCls} flex min-h-0 flex-1 items-center justify-center p-8`}>
          <DsEmptyState
            title={
              isArrears
                ? `No ${kind.toUpperCase()} arrears`
                : `No ${meta.title.replace(" Statements", "")} contributions`
            }
            description={
              isArrears
                ? `No LOP reversal or salary revision arrears with ${kind.toUpperCase()} applicability in ${formatPayMonthLabel(payMonth)}${
                    branchFilter && branchFilter !== "__ALL__"
                      ? ` for branch “${branchFilter}”`
                      : ""
                  }.`
                : branchFilter && branchFilter !== "__ALL__"
                  ? `No ${kind.toUpperCase()} amounts for branch “${branchFilter}” in ${formatPayMonthLabel(payMonth)}.`
                  : `No ${kind.toUpperCase()} amounts found in processed payroll for ${formatPayMonthLabel(payMonth)}.`
            }
          />
        </div>
      ) : (
        <div className={`${cardCls} flex min-h-0 flex-1 flex-col overflow-hidden`}>
          {error ? (
            <p className="shrink-0 border-b border-[hsl(var(--ds-border))] bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {error}
            </p>
          ) : null}
          {isArrears ? (
            <p className="shrink-0 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Arrears mode — LOP reversal + salary revision arrears only
              {kind === "pf"
                ? ". ECR uses EPFO 8-field arrear format (#~#)."
                : ". MC Return uses the same template as Regular."}
            </p>
          ) : null}
          {deferredSearch.trim() ? (
            <p className="shrink-0 border-b border-[hsl(var(--ds-border))] px-3 py-1.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Showing {tableRows.length} of {rows.length} employees · totals and downloads include all{" "}
              {rows.length}
            </p>
          ) : null}
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="ds-table w-full min-w-[720px] text-xs">
              <thead className="sticky top-0 z-10 bg-[hsl(var(--ds-muted))] shadow-[0_1px_0_hsl(var(--ds-border))]">
                {kind === "esi" ? (
                  <tr>
                    <th className="px-2 py-2 text-left">Emp Code</th>
                    <th className="px-2 py-2 text-left">Name</th>
                    <th className="px-2 py-2 text-left">ESI IP</th>
                    <th className="px-2 py-2 text-left">Branch</th>
                    <th className="px-2 py-2 text-right">Paid days</th>
                    <th className="px-2 py-2 text-right">
                      {isArrears ? "Arrears wages" : "Gross"}
                    </th>
                    <th className="px-2 py-2 text-right">Employee</th>
                    <th className="px-2 py-2 text-right">Employer</th>
                    <th className="px-2 py-2 text-right">Total</th>
                  </tr>
                ) : kind === "pt" ? (
                  <tr>
                    <th className="px-2 py-2 text-left">Emp Code</th>
                    <th className="px-2 py-2 text-left">Name</th>
                    <th className="px-2 py-2 text-left">Branch</th>
                    <th className="px-2 py-2 text-left">State</th>
                    <th className="px-2 py-2 text-right">Paid days</th>
                    <th className="px-2 py-2 text-right">Gross</th>
                    <th className="px-2 py-2 text-right">PT</th>
                  </tr>
                ) : isArrears ? (
                  <tr>
                    <th className="px-2 py-2 text-left">Emp Code</th>
                    <th className="px-2 py-2 text-left">Name</th>
                    <th className="px-2 py-2 text-left">UAN</th>
                    <th className="px-2 py-2 text-left">Branch</th>
                    <th className="px-2 py-2 text-right">Rev. arrears</th>
                    <th className="px-2 py-2 text-right">LOP rev.</th>
                    <th className="px-2 py-2 text-right">EPF wages</th>
                    <th className="px-2 py-2 text-right">EPS wages</th>
                    <th className="px-2 py-2 text-right">EE share</th>
                    <th className="px-2 py-2 text-right">ER share</th>
                    <th className="px-2 py-2 text-right">EPS share</th>
                  </tr>
                ) : (
                  <tr>
                    <th className="px-2 py-2 text-left">Emp Code</th>
                    <th className="px-2 py-2 text-left">Name</th>
                    <th className="px-2 py-2 text-left">UAN</th>
                    <th className="px-2 py-2 text-left">PF Member</th>
                    <th className="px-2 py-2 text-left">Branch</th>
                    <th className="px-2 py-2 text-right">Paid days</th>
                    <th className="px-2 py-2 text-right">PF wages</th>
                    <th className="px-2 py-2 text-right">PF (EE)</th>
                    <th className="px-2 py-2 text-right">EPF (ER)</th>
                    <th className="px-2 py-2 text-right">EPS</th>
                    <th className="px-2 py-2 text-right">EDLI</th>
                    <th className="px-2 py-2 text-right">Admin</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {!tableRows.length ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-3 py-8 text-center text-xs text-[hsl(var(--ds-muted-fg))]"
                    >
                      No employees match “{deferredSearch.trim()}”. Try name, emp code, UAN or PAN.
                    </td>
                  </tr>
                ) : null}
                {tableRows.map((r) =>
                  kind === "esi" ? (
                    <tr key={r.empCode} className="border-t border-[hsl(var(--ds-border))]">
                      <td className="px-2 py-1.5 font-medium">{r.empCode}</td>
                      <td className="px-2 py-1.5">{r.name}</td>
                      <td className="px-2 py-1.5">{r.esiNo || "—"}</td>
                      <td className="px-2 py-1.5">{displayLabel(r.branch)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{roundRupee(r.paidDays)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.grossPay))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.employeeEsi))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.employerEsi))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                        {formatInr(roundRupee(r.totalEsi))}
                      </td>
                    </tr>
                  ) : kind === "pt" ? (
                    <tr key={r.empCode} className="border-t border-[hsl(var(--ds-border))]">
                      <td className="px-2 py-1.5 font-medium">{r.empCode}</td>
                      <td className="px-2 py-1.5">{r.name}</td>
                      <td className="px-2 py-1.5">{displayLabel(r.branch)}</td>
                      <td className="px-2 py-1.5">{r.state || "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{roundRupee(r.paidDays)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.grossPay))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                        {formatInr(roundRupee(r.pt))}
                      </td>
                    </tr>
                  ) : isArrears ? (
                    <tr key={r.empCode} className="border-t border-[hsl(var(--ds-border))]">
                      <td className="px-2 py-1.5 font-medium">{r.empCode}</td>
                      <td className="px-2 py-1.5">{r.name}</td>
                      <td className="px-2 py-1.5">{r.uan || "—"}</td>
                      <td className="px-2 py-1.5">{displayLabel(r.branch)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatInr(roundRupee(r.revisionArrears))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatInr(roundRupee(r.lopReversal))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatInr(roundRupee(r.pfWages))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatInr(roundRupee(r.epsWages))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatInr(roundRupee(r.employeePf))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatInr(roundRupee(r.employerEpf))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                        {formatInr(roundRupee(r.eps))}
                      </td>
                    </tr>
                  ) : (
                    <tr key={r.empCode} className="border-t border-[hsl(var(--ds-border))]">
                      <td className="px-2 py-1.5 font-medium">{r.empCode}</td>
                      <td className="px-2 py-1.5">{r.name}</td>
                      <td className="px-2 py-1.5">{r.uan || "—"}</td>
                      <td className="px-2 py-1.5">{r.pfAccountNo || "—"}</td>
                      <td className="px-2 py-1.5">{displayLabel(r.branch)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{roundRupee(r.paidDays)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.pfWages))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.employeePf))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.employerEpf))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.eps))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.edli))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(roundRupee(r.adminCharges))}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
